import type { ChangeStorageTierParams } from "@/apiClient/models/ChangeStorageTierParams";
import type { DeadLetterGroupResponse } from "@/apiClient/models/DeadLetterGroupResponse";
import type { TieringJobResponse } from "@/apiClient/models/TieringJobResponse";
import AssetPathList, {
  useAssetPathsByPublicId,
} from "@/components/storageOps/AssetPathList";
import JobSummary, { CopyableJobId } from "@/components/storageOps/JobSummary";

// Sentinel for the jobless group (ingest-default automatic tiering) in the
// pendingJobAction slot; real job public ids never collide with it.
export const AUTOMATIC_TIERING_ACTION = "automatic-tiering";

function DeadLettersPanel({
  deadLetters,
  jobs,
  isLoading,
  onRetryFailed,
  onRetryAutomatic,
  pendingJobAction,
}: {
  deadLetters: DeadLetterGroupResponse[];
  jobs: TieringJobResponse[];
  isLoading: boolean;
  onRetryFailed: (jobPublicId: string) => void;
  onRetryAutomatic: () => void;
  pendingJobAction: string | null;
}) {
  const jobsByPublicId = new Map(
    jobs.map((job) => [job.public_id ?? "", job])
  );

  // One batched path lookup for every sample asset across all groups.
  const pathByPublicId = useAssetPathsByPublicId(
    deadLetters.flatMap((group) => group.sample_asset_public_ids)
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-5 py-3">
        <h2 className="text-lg font-semibold text-blue-200">Dead Letters</h2>
        <p className="text-xs text-grey-200">
          Rows that exhausted retries, grouped by originating job.
        </p>
      </div>

      {isLoading ? (
        <p className="px-5 py-4 text-sm text-grey-200">Loading...</p>
      ) : deadLetters.length === 0 ? (
        <p className="px-5 py-4 text-sm text-grey-200">
          No failed rows. All clear.
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-[11px] font-bold uppercase tracking-[0.08em] text-blue-100">
              <th className="px-5 py-2">Job</th>
              <th className="px-5 py-2 text-right">Failed Links</th>
              <th className="px-5 py-2">Sample Assets</th>
              <th className="px-5 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {deadLetters.map((group) => {
              const actionKey =
                group.job_public_id ?? AUTOMATIC_TIERING_ACTION;
              const job = group.job_public_id
                ? jobsByPublicId.get(group.job_public_id)
                : undefined;
              return (
                <tr
                  key={actionKey}
                  className="border-b border-slate-50 align-top text-blue-300"
                >
                  <td className="px-5 py-2">
                    {group.job_public_id ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <JobSummary
                          params={
                            job?.params as
                              | Partial<ChangeStorageTierParams>
                              | null
                              | undefined
                          }
                        />
                        <CopyableJobId publicId={group.job_public_id} />
                      </span>
                    ) : (
                      <span className="text-grey-200">
                        Automatic tiering (new uploads)
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2 text-right text-rose-700">
                    {group.failed_links.toLocaleString()}
                  </td>
                  <td className="px-5 py-2 text-xs text-grey-200">
                    {group.sample_asset_public_ids.length > 0 ? (
                      <AssetPathList
                        label=""
                        publicIds={group.sample_asset_public_ids}
                        pathByPublicId={pathByPublicId}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-5 py-2 text-right">
                    <button
                      type="button"
                      className="ox-button !mt-0 inline-flex w-auto !rounded-[8px] !px-3 !py-1 !text-xs"
                      disabled={pendingJobAction === actionKey}
                      onClick={() =>
                        group.job_public_id
                          ? onRetryFailed(group.job_public_id)
                          : onRetryAutomatic()
                      }
                    >
                      Retry failed
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default DeadLettersPanel;
